import React, { useEffect, useState } from 'react';

import { BusinessError } from '../enums/buisnessError';
import { Turnstile } from '@marsidev/react-turnstile';
import i18next from 'i18next';
import { letterState } from '../atoms/letter';
import { useForm } from 'react-hook-form';
import { useGetLetter } from '../hooks/letters';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';

interface StepEncodeLetterCodeProps {
	title: string;
	isActive?: boolean;
	goToStep?: (step: number) => void;
}
function StepEncodeLetterCode({
	title,
	isActive,
	goToStep,
}: StepEncodeLetterCodeProps) {
	const { t } = useTranslation();
	const [letterReferenceCode, setLetterReferenceCode] = useState();
	const setLetter = useSetRecoilState(letterState);
	const [captchaToken, setCaptchaToken] = useState<string>();
	const {
		isFetching,
		data: letter,
		isError,
		error,
	} = useGetLetter(letterReferenceCode);

	useEffect(() => {
		if (letter && isActive) {
			setLetter(letter.data);
			goToStep!(2);
		}
	}, [letter, goToStep, setLetter, isActive]);

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		defaultValues: {
			letterCode: new URLSearchParams(window.location.search).get(
				'letter-ref-code',
			),
		},
	});

	const onSubmit = (data: any) => {
		if (captchaToken || process.env.REACT_APP_ENV !== 'prod') {
			setLetterReferenceCode(data.letterCode);
		}
	};

	const handleCaptchaCallback = (token: string) => {
		setCaptchaToken(token);
		localStorage.setItem('captchaToken', token);
	};

	return (
		<div className='container encode-letter-code'>
			<div className='row'>
				<div className='col-lg-10 col-xxl-8 offset-lg-1 offset-xxl-2'>
					<h2 className='visually-hidden'>{title}</h2>
				</div>
				<div className='encode-letter-code__reference'>
					<p className='encode-letter-code__reference-intro'>
						{t('encode-letter-code.invite')}
					</p>
					<div className='row'>
						<div className='col-md-6'>
							<div className='encode-letter-code__stay-prepare'>
								<h2>{t('encode-letter-code.stay.prepare.title')} </h2>
								<p>
									{t('encode-letter-code.stay.prepare.safety')}
									<br />
									<a
										className='encode-letter-code__stay-prepare__question'
										href={t(
											'encode-letter-code.stay.prepare.safety.question.url',
										)}
										rel='noreferrer'
										target='_blank'
										title={t('encode-letter-code.stay.prepare.safety.question')}
									>
										{t('encode-letter-code.stay.prepare.safety.question')}
									</a>
								</p>
								<p>
									{t('encode-letter-code.stay.prepare.safety.usage')}
									<br />
									<a
										className='encode-letter-code__stay-prepare__question'
										href={t(
											'encode-letter-code.stay.prepare.safety.usage.question.url',
										)}
										rel='noreferrer'
										target='_blank'
										title={t(
											'encode-letter-code.stay.prepare.safety.usage.question',
										)}
									>
										{t('encode-letter-code.stay.prepare.safety.usage.question')}
									</a>
								</p>
							</div>
						</div>
						<div className='col-md-6 mt-5 mt-md-0 '>
							<div className='encode-letter-code__stay-prepare'>
								<p>{t('encode-letter-code.stay.prepare.encode.letter-code')}</p>
								<form className='form' onSubmit={handleSubmit(onSubmit)}>
									<div>
										{errors.letterCode?.type === 'required' && (
											<span
												data-test-id='letter-code-input-error-required'
												className='input__error'
											>
												{t(
													'encode-letter-code.stay.prepare.encode.error.letter-code.invalid',
												)}
											</span>
										)}
										{isError &&
											(error as any).response.data.errorCode ===
												BusinessError.REF_NOT_EXISTS && (
												<span
													data-test-id='letter-code-input-error-ref-not-exit'
													className='input__error'
												>
													{t(
														'encode-letter-code.stay.prepare.encode.error.letter-code.not-exist',
													)}
												</span>
											)}
										{isError &&
											(error as any).response.data.errorCode ===
												BusinessError.REF_CLOSED && (
												<span
													data-test-id='letter-code-input-error-ref-closed'
													className='input__error'
												>
													{t(
														'encode-letter-code.stay.prepare.encode.error.letter-code.ref-closed',
													)}
												</span>
											)}
										{isError &&
											(error as any).response.data.errorCode ===
												BusinessError.REF_EXPIRED && (
												<span
													data-test-id='letter-code-input-error-ref-expired'
													className='input__error'
												>
													{t(
														'encode-letter-code.stay.prepare.encode.error.letter-code.ref-expired',
													)}
												</span>
											)}
										{isError &&
											(error as any).response.data.errorCode ===
												BusinessError.CAPTCHA_NOT_VALID && (
												<span
													data-test-id='letter-code-input-error-ref-not-exit'
													className='input__error'
												>
													{t(
														'encode-letter-code.stay.prepare.encode.error.captcha.not.valid',
													)}
												</span>
											)}
									</div>

									<label className='input__label' htmlFor='letterCode'>
										{t(
											'encode-letter-code.stay.prepare.encode.letter-code.label',
										)}
									</label>
									<input
										className='input'
										data-test-id='letter-code-input'
										autoComplete='off'
										id='letterCode'
										type='number'
										{...register('letterCode', {
											required: true,
										})}
									/>
									<div className='input__description'>
										<a
											href={t('encode-index.how.to.read.question.url')}
											className='link'
											rel='noreferrer'
											target='_blank'
										>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												fill='none'
												viewBox='0 0 24 24'
												strokeWidth='2'
												stroke='currentColor'
												className='icon link__icon'
											>
												<path
													strokeLinecap='round'
													strokeLinejoin='round'
													d='m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z'
												/>
											</svg>

											<span className='link__text'>
												{t(
													'encode-letter-code.stay.prepare.how.to.read.index.question',
												)}
											</span>
										</a>
									</div>
									<div className='form__actions'>
										<Turnstile
											siteKey={
												process.env.REACT_APP_CLOUDFLARE_API_KEY as string
											}
											className='mb-4'
											options={{
												theme: 'light',

												language: i18next.language,
											}}
											onSuccess={handleCaptchaCallback}
										/>
										<button
											data-test-id='letter-code-btn-submit'
											className='btn btn--primary justify-content-between'
											type='submit'
											disabled={isFetching || !captchaToken}
										>
											<span className='btn__text'>{t('common.next')}</span>
											<span className='icon btn__icon'>
												<svg
													xmlns='http://www.w3.org/2000/svg'
													fill='none'
													width='14'
													height='14'
													viewBox='0 0 24 24'
													strokeWidth='3'
													stroke='currentColor'
												>
													<path
														strokeLinecap='round'
														strokeLinejoin='round'
														d='m8.25 4.5 7.5 7.5-7.5 7.5'
													/>
												</svg>
											</span>
										</button>
									</div>
								</form>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default StepEncodeLetterCode;
